import { useContext } from "react";
import Contexts from "../contexts/Contexts";

/**
 * The gifting kill-switch, in two halves.
 *
 * HALF 1 — the BUILD-TIME flag (this const). `REACT_APP_GIFTING_ENABLED` is baked in at
 * build time, so it evaluates once at module load to a boolean that is FIXED for the life
 * of the bundle. It is unset in both .env.development and .env.production.
 *
 * THIS IS A RUNTIME GATE, NOT DEAD-CODE ELIMINATION. Gating on it does NOT byte-strip the
 * gifting code: CRA's DefinePlugin emits `process.env` as an object literal and Terser does
 * not fold the member access, so the flag-gated branches (and their imports, e.g. GiftBell)
 * still ship in the bundle — verified empirically (the `REACT_APP_GIFTING_ENABLED` token
 * survives in flag-off main.js). They are simply UNREACHABLE while the flag is false. Dark
 * here means dormant, not absent.
 *
 * HALF 2 — the RUNTIME flag. The server now owns the real switch: `gifting_enabled` on
 * GET /api/user/info/ (UserInfoSerializer). It is a GLOBAL config.Setting row, fail-safe
 * OFF — NOT per-user, so no cohort or percentage rollout is available through it.
 * fetchUserData stamps it onto the user; App.tsx passes it to `Contexts.giftingEnabled` raw.
 *
 * EFFECTIVE = BUILD_TIME && RUNTIME, ANDed below with the build flag FIRST, so the runtime
 * value can only ever NARROW what the build allows, never widen it. A flag-off build stays
 * fully dark no matter what the API returns.
 *
 * WHY THERE IS A THIRD STATE (the whole point — do not collapse this to a boolean)
 * The app boots from a CACHED user: router.tsx `initializeApp` hydrates from localStorage,
 * flips appLoading=false in the same tick, and defers the real fetch to
 * requestIdleCallback(timeout: 2000). So there is a window — up to ~2s plus a round-trip —
 * in which we simply DO NOT KNOW the server's answer. Worse, the 7-day user cache serializes
 * `giftingEnabled` along with the user, so a returning visitor can boot carrying a flag value
 * baked by a PREVIOUS build. `Contexts.giftingEnabled` is therefore untrustworthy until
 * `Contexts.userInfoResolved` flips.
 *
 * Treating that unknown as a plain `false` is not a cosmetic bug:
 *   - a route guard would <Navigate> a legitimate visitor off /perfil/presentes — the only
 *     screen showing the share-links they JUST PAID for — and `replace` erases the history
 *     entry, so Back does not save them;
 *   - the Cart would strip gifts[] from a PAID order, silently, leaving the buyer holding
 *     cotas they believed they had gifted.
 * Hence "undetermined": each caller decides what to do while the answer is unknown. Hiding an
 * affordance is safe (it pops in a moment later). Redirecting, or deciding a checkout payload,
 * is not.
 */
export const GIFTING_ENABLED: boolean =
  process.env.REACT_APP_GIFTING_ENABLED === "true";

export type GiftingGate = "on" | "off" | "undetermined";

/**
 * The single source of truth for "is gifting available right now?".
 *
 * - "on"           — build flag on AND the server said yes. Render and fetch freely.
 * - "off"          — build flag off, OR the server said no. Behave as if the feature does not
 *                    exist. This is today's prod: the build flag is off, so the hook returns
 *                    here synchronously at first render, never once consulting the (possibly
 *                    stale) context VALUE — no gifting UI, no gifting fetch, no spinner, no
 *                    blocked checkout. The useContext call itself still runs, as it must:
 *                    a hook cannot sit behind a conditional.
 * - "undetermined" — build flag on, but GET /api/user/info/ has not settled. NEVER redirect
 *                    and NEVER decide a checkout payload in this state.
 *
 * Fetch-triggering effects MUST put the gate in their dependency array: the fetch callbacks
 * they depend on are stable useCallbacks, so without it the effect never re-runs after the
 * gate flips and the page strands on its skeleton forever.
 */
export const useGiftingGate = (): GiftingGate => {
  const { giftingEnabled, userInfoResolved } = useContext(Contexts);

  // Build-time AND, first and short-circuiting: a flag-off build never reaches the runtime
  // value, so prod behaviour is byte-for-byte identical to before gifting became server-driven.
  if (!GIFTING_ENABLED) return "off";
  if (!userInfoResolved) return "undetermined";
  return giftingEnabled ? "on" : "off";
};
